import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContentNoPortal,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ChevronsUpDown, Check } from "lucide-react";
import { cn } from "@/lib/utils";

export function CreateActivityDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { toast } = useToast();
  const [type, setType] = useState("task");
  const [body, setBody] = useState("");
  const [dealId, setDealId] = useState("");
  const [contactId, setContactId] = useState("");
  const [contactComboOpen, setContactComboOpen] = useState(false);
  const [assignedTo, setAssignedTo] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [reminderAt, setReminderAt] = useState("");

  const { data: deals } = useQuery<any[]>({
    queryKey: ["/api/crm/deals"],
    enabled: open,
  });

  const { data: contactsResponse } = useQuery<{ data: any[]; total: number }>({
    queryKey: ["/api/contacts?limit=200"],
    enabled: open,
  });
  const contacts = contactsResponse?.data || [];

  const { data: users } = useQuery<any[]>({
    queryKey: ["/api/users"],
    enabled: open,
  });

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest("POST", "/api/crm/activities", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/activities/my"] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/activities/due"] });
      onOpenChange(false);
      setBody("");
      setDealId("");
      setContactId("");
      setAssignedTo("");
      setDueAt("");
      setReminderAt("");
      toast({ title: "Activity created" });
    },
    onError: (err: any) => {
      toast({ title: "Failed to create activity", description: err.message, variant: "destructive" });
    },
  });

  const toISOLocal = (val: string) => val ? new Date(val).toISOString() : null;

  const handleSubmit = () => {
    createMutation.mutate({
      type,
      body: body || null,
      dealId: (dealId && dealId !== "none") ? dealId : null,
      contactId: (contactId && contactId !== "none") ? contactId : null,
      assignedTo: (assignedTo && assignedTo !== "me") ? assignedTo : null,
      dueAt: toISOLocal(dueAt),
      reminderAt: toISOLocal(reminderAt),
    });
  };

  const selectedContact = contacts.find((c: any) => c.id === contactId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create Activity</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="select-activity-type-qa">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="task">Task</SelectItem>
                <SelectItem value="call">Call</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="sms">SMS</SelectItem>
                <SelectItem value="meeting">Meeting</SelectItem>
                <SelectItem value="note">Note</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder="Activity description..."
              data-testid="input-activity-body-qa"
            />
          </div>
          <div>
            <Label>Due Date</Label>
            <Input
              type="datetime-local"
              value={dueAt}
              onChange={e => setDueAt(e.target.value)}
              data-testid="input-activity-due-qa"
            />
          </div>
          <div>
            <Label>Reminder</Label>
            <Input
              type="datetime-local"
              value={reminderAt}
              onChange={e => setReminderAt(e.target.value)}
              data-testid="input-activity-reminder-qa"
            />
          </div>
          <div>
            <Label>Linked Deal</Label>
            <Select value={dealId} onValueChange={setDealId}>
              <SelectTrigger data-testid="select-activity-deal-qa">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {(deals || []).map((d: any) => (
                  <SelectItem key={d.id} value={d.id}>{d.name || d.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Linked Contact</Label>
            <Popover open={contactComboOpen} onOpenChange={setContactComboOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={contactComboOpen}
                  className="w-full justify-between font-normal"
                  data-testid="select-activity-contact-qa"
                >
                  <span className={cn(!selectedContact && "text-muted-foreground")}>
                    {selectedContact ? (selectedContact.name || selectedContact.email) : "None"}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContentNoPortal className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search contacts..." data-testid="input-search-contact-qa" />
                  <CommandList>
                    <CommandEmpty>No contacts found.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="none"
                        onSelect={() => {
                          setContactId("");
                          setContactComboOpen(false);
                        }}
                      >
                        <Check className={cn("mr-2 h-4 w-4", !contactId ? "opacity-100" : "opacity-0")} />
                        None
                      </CommandItem>
                      {contacts.map((c: any) => (
                        <CommandItem
                          key={c.id}
                          value={`${c.name || ""} ${c.email || ""} ${c.phoneNumber || ""}`}
                          onSelect={() => {
                            setContactId(c.id === contactId ? "" : c.id);
                            setContactComboOpen(false);
                          }}
                        >
                          <Check className={cn("mr-2 h-4 w-4", contactId === c.id ? "opacity-100" : "opacity-0")} />
                          <span>{c.name || c.email}</span>
                          {c.phoneNumber && (
                            <span className="ml-1.5 text-xs text-muted-foreground">{c.phoneNumber}</span>
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContentNoPortal>
            </Popover>
          </div>
          <div>
            <Label>Assign To</Label>
            <Select value={assignedTo} onValueChange={setAssignedTo}>
              <SelectTrigger data-testid="select-activity-assignee-qa">
                <SelectValue placeholder="Me" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="me">Me</SelectItem>
                {(users || []).map((u: any) => (
                  <SelectItem key={u.id} value={u.id}>{u.displayName || u.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={createMutation.isPending} data-testid="button-submit-activity-qa">
            {createMutation.isPending ? "Creating..." : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
